// Etappe 4.8 — Specifika villkor för inskickat material (anslag, evenemang, foton, ändringsförslag, kartmarkeringar).
// Kompletterar /anvandarvillkor (allmänna villkor) med en tydligare, vänligare och kortare text
// som besökare ser direkt vid inskickning. Skrivs i lugn, lokal ton — ingen tung juridik.
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function VillkorInskickat() {
  useEffect(() => {
    document.title = "Villkor för inskickat material – vidalv.live";
    window.scrollTo(0, 0);
  }, []);

  return (
    <article data-testid="legal-villkor-inskickat" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-pine hover:text-pine-hover no-underline mb-6">
        <ArrowLeft size={14} /> Tillbaka till startsidan
      </Link>

      <h1 className="font-fraktur-smooth text-falu text-3xl sm:text-4xl mb-2">Villkor för inskickat material</h1>
      <p className="text-xs text-bark/60 mb-8">Senast uppdaterad: 28 juni 2026</p>

      <section className="space-y-5 text-ink leading-relaxed text-sm sm:text-base">
        <p>
          När du skickar in ett anslag, ett evenemang, ett tips, ett ändringsförslag eller ett foto
          till vidalv.live gäller följande korta villkor. De är till för att informationen ska hålla
          god kvalitet — och för att du som skickar in ska veta vad som händer med ditt material.
        </p>

        <div className="space-y-2">
          <h2 className="font-heading text-lg font-semibold text-ink">Redaktionell granskning</h2>
          <p>
            Allt material granskas av vidalv.live-redaktionen innan det publiceras. Vi kan välja
            att <strong>publicera, korrigera, korta ned</strong> eller <strong>avstå</strong> från att publicera. Du har ingen
            automatisk rätt till publicering, men vi gör vårt bästa för att hjälpa lokala
            informationer fram.
          </p>
        </div>

        <div className="space-y-2">
          <h2 className="font-heading text-lg font-semibold text-ink">Du måste vara berättigad</h2>
          <p>
            Du intygar att du har rätt att dela det material du skickar in — text, bild, video eller
            länk. Material som du själv tagit fram (egna foton, egna beskrivningar) är vanligen helt
            okej. Material som hör till någon annan får inte skickas in utan dennes medgivande.
          </p>
        </div>

        <div className="space-y-2">
          <h2 className="font-heading text-lg font-semibold text-ink">Foton och video av människor</h2>
          <p>
            Vid foton eller filmer där personer känns igen ska du säkerställa att personerna är okej
            med att publiceras lokalt. Bilder på barn kräver särskild eftertanke och målsmans
            godkännande.
          </p>
        </div>

        <div className="space-y-2">
          <h2 className="font-heading text-lg font-semibold text-ink">YouTube och andra länkar</h2>
          <p>
            Du får gärna föreslå <strong>länkar till befintliga YouTube-videor, webbsidor och sociala
            medier</strong> — vi visar dem som länk eller inbäddat referensformat. Vi kopierar inte fremda
            videor som egna filer, klipper inte ned dem och publicerar inte ut dem under eget namn.
            Redaktionen kan korta texter, men inte göra om någon annans video.
          </p>
        </div>

        <div className="space-y-2">
          <h2 className="font-heading text-lg font-semibold text-ink">Inte sensitiva personuppgifter</h2>
          <p>
            Skicka inte in känsliga personuppgifter (t.ex. uppgifter om hälsa, politiska åsikter,
            religiös tillhörighet eller liknande). Skicka inte heller in privata uppgifter om andra
            personer utan deras medgivande.
          </p>
        </div>

        <div className="space-y-2">
          <h2 className="font-heading text-lg font-semibold text-ink">Vad vi inte publicerar</h2>
          <p>
            Vi publicerar inte material som är kränkande, hotfullt, diskriminerande, olagligt eller
            som i övrigt strider mot god lokal ton. Reklam, partipolitisk propaganda och spam
            avvisas.
          </p>
        </div>

        <div className="space-y-2">
          <h2 className="font-heading text-lg font-semibold text-ink">Rättelse och borttagning</h2>
          <p>
            Är något fel, har vi missuppfattat något, eller vill du att ditt material tas bort?
            Skicka ett mejl till{" "}
            <a href="mailto:redaktion@vidalv.live" className="text-falu hover:text-falu-hover">redaktion@vidalv.live</a> så hjälper vi dig så snart vi kan.
          </p>
        </div>

        <div className="space-y-2">
          <h2 className="font-heading text-lg font-semibold text-ink">Vidare läsning</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><Link to="/integritet" className="text-falu hover:text-falu-hover">Integritet – hantering av personuppgifter</Link></li>
            <li><Link to="/anvandarvillkor" className="text-falu hover:text-falu-hover">Allmänna användarvillkor</Link></li>
            <li><Link to="/om-oss" className="text-falu hover:text-falu-hover">Om oss och kontakt</Link></li>
          </ul>
        </div>

        <p className="text-xs text-bark/60 italic pt-4 border-t border-warm-border">
          vidalv.live drivs lokalt av Hodema Edertools AB. Sidan är inte en kommunal eller statlig
          myndighet, utan en lokal informationsplattform för Backe, Rossön och bygden runt omkring.
        </p>
      </section>
    </article>
  );
}
